import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, Holiday, HolidayNote, HolidaysData } from './crmApi';
import { Empty, Spinner, inputCls, labelCls, purpleBg } from './ui';

// ── Holidays ─────────────────────────────────────────────────
// A Lead Manager, Sales Admin or the admin closes the sales office for a day
// (or a few days in a row): no leads go out, nothing moves to the next
// salesperson, no reminders, no EOD. Follow-ups set for those days move to
// the next working day at the same time. Institute holidays (the admin's
// Mentor Calendar) close the CRM too; they show here but change only there.
// Mirrors the app's crm_holidays_screen.dart.

const NAMES = ['Diwali', 'Pongal', 'Ayudha Pooja', 'Christmas', 'New Year', 'Office closed'];
const todayIST = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);

// Days in a row with the same name show as one: "Wed 21 – Thu 22 Oct · Diwali"
function runs(list: Holiday[]): Holiday[][] {
  const out: Holiday[][] = [];
  for (const h of list) {
    const last = out[out.length - 1];
    const prev = last?.[last.length - 1];
    const gap = prev ? (Date.parse(h.date) - Date.parse(prev.date)) / 86400e3 : 99;
    // a Sunday between two days of one holiday doesn't split it
    if (prev && prev.name === h.name && prev.kind === h.kind && gap <= 2) last.push(h);
    else out.push([h]);
  }
  return out;
}
const runLabel = (r: Holiday[]) => (r.length === 1 ? r[0].label : `${r[0].label} – ${r[r.length - 1].label}`);

// The note on Today, the home pages and Team today: a holiday today, or one this week
export const HolidayBanner: React.FC<{ note?: HolidayNote | null; manager?: boolean; onOpen?: () => void }> = ({ note, manager, onOpen }) => {
  if (!note || (!note.today && !note.next)) return null;
  const h = note.today || note.next!;
  const days = h.until !== h.date ? `${h.label} – ${h.untilLabel}` : h.label;
  const link = onOpen && <button onClick={onOpen} className="ml-1 font-bold underline underline-offset-2">Holidays →</button>;
  if (note.today) {
    return (
      <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-3" role="status">
        <p className="text-sm font-bold text-amber-900">🏖️ {note.today.until !== note.today.date ? `Holiday till ${note.today.untilLabel}` : 'Today is a holiday'}: {h.name}</p>
        <p className="text-xs text-amber-800 mt-0.5">
          The office is closed{manager ? ': no leads go out, nothing moves on, no reminders.' : ': no calls today.'} Work starts again on {h.backLabel}.{link}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-2.5" role="status">
      <p className="text-xs text-amber-900"><b>🏖️ Holiday coming: {days} ({h.name}).</b> The office is closed{manager ? ', so no leads go out' : ''}; follow-ups that day go to {h.backLabel}.{link}</p>
    </div>
  );
};

const KIND: Record<Holiday['kind'], { chip: string; label: string }> = {
  office: { chip: 'bg-amber-100 text-amber-800', label: 'Office holiday' },
  institute: { chip: 'bg-sky-100 text-sky-800', label: 'Institute holiday' },
};

const HolidayRows: React.FC<{ list: Holiday[]; onRemove?: (h: Holiday) => void; busy?: string }> = ({ list, onRemove, busy }) => (
  <ul className="divide-y divide-gray-100">
    {runs(list).map(r => {
      const h = r[0];
      return (
        <li key={h.date} className={`py-2.5 flex items-center gap-3 flex-wrap ${h.past ? 'opacity-50' : ''}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800">{runLabel(r)}{r.some(x => x.isToday) && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">today</span>}</p>
            <p className="text-xs text-gray-500">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mr-1.5 ${KIND[h.kind].chip}`}>{KIND[h.kind].label}</span>
              {h.kind === 'office' ? `${h.name}${h.byName ? ` · added by ${h.byName}` : ''}` : 'Set by the admin on the Mentor Calendar'}
            </p>
          </div>
          {onRemove && h.kind === 'office' && !h.past && (
            <div className="flex gap-1.5 flex-wrap">
              {r.map(x => (
                <button key={x.date} onClick={() => onRemove(x)} disabled={!!busy}
                  className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-700 disabled:opacity-50">
                  {busy === x.date ? '…' : r.length > 1 ? `Take off ${x.label}` : 'Take off'}
                </button>
              ))}
            </div>
          )}
        </li>
      );
    })}
  </ul>
);

// Managers: add a holiday, see them all, take one off
export const CrmHolidaysManager: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [data, setData] = useState<HolidaysData | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [several, setSeveral] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { setData(await crm.holidays(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!from) { toast.error('Pick the day of the holiday'); return; }
    if (several && (!to || to < from)) { toast.error('Pick the last day (on or after the first)'); return; }
    setSaving(true);
    try {
      const r = await crm.addHoliday(auth, { from, to: several ? to : undefined, name: name.trim() });
      setData(r);
      const days = r.added.length === 1 ? r.holidays.find(h => h.date === r.added[0])?.label || r.added[0] : `${r.added.length} days`;
      toast.success(`${days} added${r.moved ? ` · ${r.moved} follow-up${r.moved === 1 ? '' : 's'} moved to the next working day` : ''}${r.skipped.length ? ` · skipped ${r.skipped.map(s => s.label).join(', ')}` : ''}`);
      setFrom(''); setTo(''); setName(''); setSeveral(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (h: Holiday) => {
    if (!window.confirm(`Take ${h.label} (${h.name}) off? It becomes a working day again. Follow-ups already moved for it stay where they are.`)) return;
    setBusy(h.date);
    try { setData(await crm.removeHoliday(auth, h.date)); toast.success(`${h.label} is a working day again`); }
    catch (e: any) { toast.error(e.message); }
    finally { setBusy(''); }
  };

  if (!data) return <Spinner label="Loading the holidays…" />;
  const ahead = data.holidays.filter(h => !h.past);
  const past = data.holidays.filter(h => h.past);
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500 px-1">On a holiday no leads go out, nothing moves to the next salesperson and nobody gets reminders. Follow-ups set for it move to the next working day, same time, and their salespeople are told.</p>
      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 space-y-3" role="region" aria-label="Add a holiday">
        <h2 className="font-bold text-gray-800">Add a holiday</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls} htmlFor="hol-from">{several ? 'First day' : 'Day'}</label>
            <input id="hol-from" type="date" min={todayIST()} value={from} onChange={e => setFrom(e.target.value)} className={inputCls} />
          </div>
          {several && (
            <div>
              <label className={labelCls} htmlFor="hol-to">Last day</label>
              <input id="hol-to" type="date" min={from || todayIST()} value={to} onChange={e => setTo(e.target.value)} className={inputCls} />
            </div>
          )}
          <div className={several ? '' : 'sm:col-span-2'}>
            <label className={labelCls} htmlFor="hol-name">What for</label>
            <input id="hol-name" value={name} onChange={e => setName(e.target.value)} placeholder="Diwali, office closed…" className={inputCls} list="hol-names" />
            <datalist id="hol-names">{NAMES.map(n => <option key={n} value={n} />)}</datalist>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer">
          <input type="checkbox" checked={several} onChange={e => setSeveral(e.target.checked)} className="w-4 h-4 accent-purple-700" /> More than one day in a row
        </label>
        <div>
          <button onClick={add} disabled={saving} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>{saving ? 'Adding…' : '🏖️ Add holiday'}</button>
          <p className="text-[11px] text-gray-400 mt-1.5">Today works too: leads stop going out now, and today's follow-ups move to the next working day.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="Holidays ahead">
        <h2 className="font-bold text-gray-800 mb-1">Holidays ahead</h2>
        {ahead.length ? <HolidayRows list={ahead} onRemove={remove} busy={busy} /> : <p className="text-sm text-gray-400 py-2">No holidays set. Every working day is open.</p>}
        {past.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs font-bold text-gray-500 cursor-pointer">Recent ones ({past.length})</summary>
            <HolidayRows list={past} />
          </details>
        )}
      </div>
    </div>
  );
};

// Salespeople: the holidays ahead
export const HolidayList: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [data, setData] = useState<HolidaysData | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { crm.holidays(auth).then(setData).catch(e => setError(e.message)); }, [auth]);
  if (error) return <Empty icon="⚠️" title="Could not load the holidays" sub={error} />;
  if (!data) return null;
  const ahead = data.holidays.filter(h => !h.past);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="Holidays">
      <h2 className="font-bold text-gray-800">🏖️ Holidays</h2>
      <p className="text-xs text-gray-400 mb-1">The office is closed: no calls, no new leads. Follow-ups that day go to the next working day.</p>
      {ahead.length ? <HolidayRows list={ahead} /> : <p className="text-sm text-gray-400 py-2">No holidays coming up.</p>}
    </div>
  );
};
